// src/Puzzles.js
import React, { useEffect, useState, useMemo } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import { Link } from 'react-router-dom';
import PuzzleGrid from './components/PuzzleGrid';
import { getDifficulty } from './utils/puzzleUtils';
import { puzzleAPI } from './services/api';
import './Puzzles.css';

const Puzzles = () => {
    const [puzzles, setPuzzles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isGridView, setIsGridView] = useState(false);
    const [difficultiesFilter, setDifficultiesFilter] = useState([]);
    const [gridSizeFilter, setGridSizeFilter] = useState([]);

    useEffect(() => {
        const fetchPuzzles = async () => {
            try {
                setLoading(true);
                const data = await puzzleAPI.getAllPuzzles();
                setPuzzles(data);
                setLoading(false);
            } catch (err) {
                setError('Failed to fetch puzzles');
                setLoading(false);
            }
        };

        fetchPuzzles();
    }, []);

    const columns = useMemo(
        () => [
            {
                Header: 'S.No.',
                accessor: (_, rowIndex) => rowIndex + 1,
                id: 'serialNumber',
            },
            {
                Header: 'Name',
                Cell: ({ row }) => <Link to={`/puzzle/${row.original.board_number}`}>{row.original.name}</Link>,
                accessor: 'name',
            },
            {
                Header: 'Board Number',
                accessor: 'board_number',
            },
            {
                Header: 'Booklet Number',
                accessor: 'booklet_number',
            },
            {
                Header: 'Grid Size',
                accessor: 'grid_size',
            },
            {
                Header: '# of Solutions',
                accessor: 'num_solutions',
            },
            {
                Header: 'Finesse Score',
                accessor: 'finesse_score',
            },
            {
                Header: 'Island Score',
                accessor: 'island_score',
            },
            {
                Header: 'Color Score',
                accessor: 'color_score',
            },
            {
                Header: 'Edge Score',
                accessor: 'edge_score',
            },
            {
                Header: '# of Islands',
                accessor: 'number_of_islands',
            },
            {
                Header: '# Black',
                accessor: 'number_black',
            },
            {
                Header: '# Red',
                accessor: 'number_red',
            },
            {
                Header: '# Blue',
                accessor: 'number_blue',
            },
            {
                Header: '# Yellow',
                accessor: 'number_yellow',
            },
            {
                Header: 'Difficulty',
                accessor: row => getDifficulty(row.fice_score),
            },
            {
                Header: 'Description',
                accessor: 'description',
            },
            {
                Header: 'Fun Fact',
                accessor: 'fun_fact',
            },
            {
                Header: 'FICE',
                accessor: 'fice_score',
            }
        ],
        []
    );

    const filteredPuzzles = useMemo(() => {
        return puzzles.filter(puzzle => 
            (difficultiesFilter.length === 0 || difficultiesFilter.includes(getDifficulty(puzzle.fice_score))) &&
            (gridSizeFilter.length === 0 || gridSizeFilter.includes(puzzle.pattern.length))
        );
    }, [puzzles, difficultiesFilter, gridSizeFilter]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: filteredPuzzles,
            initialState: { pageIndex: 0, pageSize: 10 },
        },
        useSortBy,
        usePagination
    );

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="puzzles-container">
            <h1>Puzzles</h1>
            <div className="filters">
                <div className="difficulty-filter">
                    {['Easy', 'Medium', 'Hard', 'Expert'].map(difficulty => (
                        <label key={difficulty}>
                            <input
                                type="checkbox"
                                checked={difficultiesFilter.includes(difficulty)}
                                onChange={() => {
                                    setDifficultiesFilter(prev =>
                                        prev.includes(difficulty)
                                            ? prev.filter(d => d !== difficulty)
                                            : [...prev, difficulty]
                                    );
                                }}
                            />
                            {difficulty}
                        </label>
                    ))}
                </div>
                <div className="grid-size-filter">
                    {[4, 5, 6, 8].map(size => (
                        <label key={size}>
                            <input
                                type="checkbox"
                                checked={gridSizeFilter.includes(size)}
                                onChange={() => {
                                    setGridSizeFilter(prev =>
                                        prev.includes(size)
                                            ? prev.filter(s => s !== size)
                                            : [...prev, size]
                                    );
                                }}
                            />
                            {size}x{size}
                        </label>
                    ))}
                </div>
                <button className="view-toggle-button" onClick={() => setIsGridView(!isGridView)}>
                    Switch to {isGridView ? 'List' : 'Grid'} View
                </button>
            </div>
            {isGridView ? (
                <PuzzleGrid puzzles={filteredPuzzles} />
            ) : (
                <div className="puzzles-table">
                    <table {...getTableProps()}>
                        <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                            {column.render('Header')}
                                            <span>
                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? ' 🔽'
                                                        : ' 🔼'
                                                    : ''}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {page.map((row, i) => {
                                prepareRow(row)
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map(cell => {
                                            return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    <div className="pagination">
                        <div className="pagination-controls">
                            <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                                {'<<'}
                            </button>
                            <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                                {'<'}
                            </button>
                            <button onClick={() => nextPage()} disabled={!canNextPage}>
                                {'>'}
                            </button>
                            <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                                {'>>'}
                            </button>
                        </div>
                        <span>
                            Page{' '}
                            <strong>
                                {pageIndex + 1} of {pageOptions.length}
                            </strong>
                        </span>
                        <span>
                            | Go to page:{' '}
                            <input
                                type="number"
                                defaultValue={pageIndex + 1}
                                onChange={e => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0
                                    gotoPage(page)
                                }}
                                style={{ width: '50px' }}
                            />
                        </span>
                        <select
                            value={pageSize}
                            onChange={e => {
                                setPageSize(Number(e.target.value))
                            }}
                        >
                            {[10, 20, 30, 40, 50].map(pageSize => (
                                <option key={pageSize} value={pageSize}>
                                    Show {pageSize}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Puzzles;
